'use client'
import React, { useEffect, useState } from "react";
import { ArrowRight, ArrowLeft, Loader2, Search, Truck } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/table/components/data-table";
import { columns2, distributionAnalyticsColumns } from "@/components/table/components/columns";
import { distributionDemoProjects, testLogDemoProjects } from "@/mocks/distributionAnalyticsDemo";
import { isMockModeActive } from "@/services/mockAuth";
import { useDeleteProjectMutation, useGetProjectsQuery } from "@/redux/slices/define/defineApiSlice";
import { useDeleteRequestItemMutation, useGetRequestItemsQuery } from "@/redux/slices/stack-and-ship/stack-and-ship-apiSlice";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { showErrorToast, showSuccessToast } from "@/components/toast/toast";
import { db } from "@/services/Database";
import { useSearchParams } from "react-router-dom";

type ModuleSelection = 'distribution' | 'test-log' | null;

const MODULE_QUERY_PARAM = 'module';

const readModuleParam = (value: string | null): ModuleSelection =>
  value === 'distribution' || value === 'test-log' ? value : null;
type ProjectScope = 'mine' | 'all';
type DataCollectionView = 'online' | 'offline';

const getOwnerName = (project: any) => (
  project?.creator?.name ||
  project?.createdBy ||
  project?.owner ||
  ''
).toString().toLowerCase();

const getProjectsFromResponse = (response: any) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.projects)) return response.projects;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.projects)) return response.data.projects;
  if (Array.isArray(response?.data?.requestItems)) return response.data.requestItems;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.requestItems)) return response.requestItems;
  return [];
};

const SelectionCard = ({
  icon: Icon,
  badge,
  title,
  description,
  points,
  onOpen,
  buttonLabel,
  tone,
}: {
  icon: React.ElementType;
  badge: string;
  title: string;
  description: React.ReactNode;
  points: string[];
  onOpen: () => void;
  buttonLabel: string;
  tone: 'blue' | 'green';
}) => {
  const isBlue = tone === 'blue';

  return (
    <div className="flex h-full min-h-[560px] flex-col rounded-lg border border-[#DCE7F5] bg-white p-10 shadow-[0_18px_40px_rgba(0,72,153,0.10)]">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div className={cn(
          "flex h-[72px] w-[72px] items-center justify-center rounded-[18px]",
          isBlue ? "bg-[#E8F0FF] text-blue" : "bg-[#E4F8EF] text-[#087B61]"
        )}>
          <Icon className="h-8 w-8" />
        </div>
        <Badge className={cn(
          "border-0 px-3 py-1 text-xs uppercase tracking-wide",
          isBlue ? "bg-[#E8F0FF] text-blue" : "bg-[#E4F8EF] text-[#087B61]"
        )}>
          {badge}
        </Badge>
      </div>

      <h1 className="mb-4 text-2xl font-bold text-[#071426]">{title}</h1>
      <p className="mb-6 max-w-[440px] text-base leading-7 text-[#607086]">{description}</p>

      <div className={cn(
        "mb-8 rounded-lg p-5",
        isBlue ? "bg-[#EAF1FF] text-[#173D8F]" : "bg-[#E4F8EF] text-[#075F4D]"
      )}>
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.12em]">Use this when you want to...</p>
        <ul className="list-disc space-y-2 pl-5 text-sm leading-6">
          {points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </div>

      <div className="mt-auto w-fit">
        <Button
          type="button"
          onClick={onOpen}
          className={cn(
            "h-11 gap-2 px-6 text-base font-semibold text-white",
            isBlue ? "bg-blue hover:bg-[#003B7D]" : "bg-[#087B61] hover:bg-[#05634E]"
          )}
        >
          {buttonLabel}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const Page = () => {
  // The top-nav Home link deep-links to a module list via ?module= when leaving a project.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedModule, setSelectedModule] = useState<ModuleSelection>(
    () => readModuleParam(searchParams.get(MODULE_QUERY_PARAM)),
  );
  const [pendingModule, setPendingModule] = useState<ModuleSelection>(null);
  const [projectScope, setProjectScope] = useState<ProjectScope>('mine');
  const [dataCollectionView, setDataCollectionView] = useState<DataCollectionView>('online');
  const [offlineProjects, setOfflineProjects] = useState<any[]>([]);
  const [isLoadingOfflineProjects, setIsLoadingOfflineProjects] = useState(false);
  const [offlineProjectsError, setOfflineProjectsError] = useState<unknown>(null);
  const [isMockMode] = useState(() => isMockModeActive());
  const [deletedMockProjectIds, setDeletedMockProjectIds] = useState<{ distribution: string[]; testLog: string[] }>({
    distribution: [],
    testLog: [],
  });
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteProject, { isLoading: isDeletingProject }] = useDeleteProjectMutation();
  const [deleteRequestItem, { isLoading: isDeletingRequestItem }] = useDeleteRequestItemMutation();
  const {
    data: distributionProjectsResponse,
    isLoading: isLoadingDistributionProjects,
    isFetching: isFetchingDistributionProjects,
    error: distributionProjectsError,
  } = useGetRequestItemsQuery({ scope: projectScope }, { skip: isMockMode || selectedModule !== 'distribution' });
  const {
    data: testLogProjectsResponse,
    isLoading: isLoadingTestLogProjects,
    isFetching: isFetchingTestLogProjects,
    error: testLogProjectsError,
  } = useGetProjectsQuery(
    { scope: projectScope },
    { skip: isMockMode || selectedModule !== 'test-log' || dataCollectionView === 'offline' },
  );

  useEffect(() => {
    if (selectedModule !== 'test-log' || dataCollectionView !== 'offline') return;

    let active = true;
    const loadOfflineProjects = async () => {
      try {
        setIsLoadingOfflineProjects(true);
        setOfflineProjectsError(null);
        const projects = await db.getAllProjects();
        if (active) setOfflineProjects(projects || []);
      } catch (error) {
        console.error('Error loading offline projects:', error);
        if (active) {
          setOfflineProjects([]);
          setOfflineProjectsError(error);
        }
      } finally {
        if (active) setIsLoadingOfflineProjects(false);
      }
    };

    void loadOfflineProjects();
    return () => {
      active = false;
    };
  }, [dataCollectionView, selectedModule]);

  const distributionProjects = isMockMode
    ? distributionDemoProjects.filter((project) => !deletedMockProjectIds.distribution.includes(String(project.id)))
    : getProjectsFromResponse(distributionProjectsResponse);
  const testLogProjects = isMockMode
    ? testLogDemoProjects.filter((project) => !deletedMockProjectIds.testLog.includes(String(project.id)))
    : getProjectsFromResponse(testLogProjectsResponse);

  const closeDeleteDialog = () => {
    setDeleteTarget(null);
    setDeleteConfirmText('');
  };

  const closeModuleDialog = () => setPendingModule(null);

  const moduleDialogDescription = pendingModule === 'distribution'
    ? 'This module compares the current package configuration against package specifications and prior test results.'
    : 'This module records and submits physical test results, built for real-time data entry.';

  const confirmModuleOpen = () => {
    setSelectedModule(pendingModule);
    setPendingModule(null);
    setProjectScope('mine');
    setDataCollectionView('online');
  };

  const openDeleteDialog = (project: any) => {
    if (project?.canDelete === false) return;
    setDeleteTarget(project);
    setDeleteConfirmText('');
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget || deleteConfirmText.trim().toLowerCase() !== 'delete') return;

    try {
      if (isMockMode) {
        if (selectedModule === 'distribution') {
          setDeletedMockProjectIds((current) => ({
            ...current,
            distribution: [...current.distribution, String(deleteTarget.id)],
          }));
        } else if (selectedModule === 'test-log') {
          setDeletedMockProjectIds((current) => ({
            ...current,
            testLog: [...current.testLog, String(deleteTarget.id)],
          }));
        }
      } else if (selectedModule === 'distribution') {
        await deleteRequestItem(deleteTarget.id).unwrap();
        showSuccessToast('Distribution Analytics project deleted successfully');
      } else if (selectedModule === 'test-log') {
        await deleteProject(deleteTarget.id).unwrap();
        showSuccessToast('Data Collection project deleted successfully');
      }

      closeDeleteDialog();
    } catch (error: any) {
      const message = error?.data?.msg || error?.data?.message || error?.message || 'Failed to delete project';
      showErrorToast(message);
    }
  };

  if (selectedModule) {
    const isDistribution = selectedModule === 'distribution';
    const isOfflineCollection = !isDistribution && dataCollectionView === 'offline';
    const moduleProjects = isDistribution
      ? distributionProjects
      : isOfflineCollection
        ? offlineProjects
        : testLogProjects;
    const moduleError = isOfflineCollection
      ? offlineProjectsError
      : !isMockMode && (isDistribution ? distributionProjectsError : testLogProjectsError);
    const moduleLoading = !isMockMode && (
      isOfflineCollection
        ? isLoadingOfflineProjects
        : isDistribution
        ? isLoadingDistributionProjects || isFetchingDistributionProjects
        : isLoadingTestLogProjects || isFetchingTestLogProjects
    );
    const visibleProjects = isOfflineCollection || !isMockMode
      ? moduleProjects
      : projectScope === 'all'
        ? moduleProjects
        : moduleProjects.filter((project) => getOwnerName(project).includes('local admin'));
    const rowsWithDeletePermission = visibleProjects.map((project) => ({
      ...project,
      canDelete: isMockMode ? getOwnerName(project).includes('local admin') : project?.canDelete,
    }));
    const deleteProjectName = deleteTarget?.project_name || deleteTarget?.projectName || 'this project';
    const isDeleting = isDeletingProject || isDeletingRequestItem;

    return (
      <div className="h-[calc(100vh-80px)] overflow-y-auto bg-[#F6FAFF] px-8 py-8">
        <div className="mx-auto max-w-[1500px] space-y-4">
          <div className="flex items-center justify-between border border-[#DCE7F5] bg-white px-5 py-4">
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="outline"
                className="h-9 gap-2"
                onClick={() => {
                  setSelectedModule(null);
                  if (searchParams.has(MODULE_QUERY_PARAM)) setSearchParams({}, { replace: true });
                }}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-blue">
                  {isDistribution ? 'Distribution Analytics Projects' : 'Data Collection Module'}
                </h1>
                <p className="text-sm text-[#52657A]">
                  {isDistribution
                    ? 'Review prior package configurations, open an existing analysis, or create a new distribution analytics project.'
                    : 'Review physical ship test records, open existing records, or create a new data collection project.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {!isDistribution && (
                <div className="flex rounded-md border border-[#C9D8EA] bg-[#F7FAFE] p-1">
                  <Button
                    type="button"
                    variant="ghost"
                    className={cn("h-8 px-4 text-sm", dataCollectionView === 'online' && "bg-blue text-white hover:bg-blue hover:text-white")}
                    onClick={() => setDataCollectionView('online')}
                  >
                    Online Projects
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className={cn("h-8 px-4 text-sm", dataCollectionView === 'offline' && "bg-blue text-white hover:bg-blue hover:text-white")}
                    onClick={() => setDataCollectionView('offline')}
                  >
                    Offline Projects
                  </Button>
                </div>
              )}
              {!isOfflineCollection && (
                <div className="flex rounded-md border border-[#C9D8EA] bg-[#F7FAFE] p-1">
                  <Button
                    type="button"
                    variant="ghost"
                    className={cn("h-8 px-4 text-sm", projectScope === 'mine' && "bg-blue text-white hover:bg-blue hover:text-white")}
                    onClick={() => setProjectScope('mine')}
                  >
                    My Projects
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className={cn("h-8 px-4 text-sm", projectScope === 'all' && "bg-blue text-white hover:bg-blue hover:text-white")}
                    onClick={() => setProjectScope('all')}
                  >
                    All Projects
                  </Button>
                </div>
              )}
              <Badge variant="outline" className="bg-[#F7FAFE] text-blue">
                {visibleProjects.length} Projects
              </Badge>
            </div>
          </div>

          <div className="border border-[#DCE7F5] bg-white">
            {moduleError ? (
              <div className="p-6 text-sm text-red-700">
                Unable to load {isDistribution ? 'Distribution Analytics' : 'Data Collection'} projects. Please retry after the API is available.
              </div>
            ) : moduleLoading ? (
              <div className="flex items-center justify-center gap-2 p-8 text-sm text-[#52657A]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading projects...
              </div>
            ) : (
              <DataTable
                isDistribution={isDistribution}
                isOffline={isOfflineCollection}
                data={rowsWithDeletePermission}
                columns={isDistribution ? distributionAnalyticsColumns : columns2}
                onMockDelete={!isOfflineCollection && isMockMode ? openDeleteDialog : undefined}
                onDeleteProject={!isOfflineCollection && !isMockMode ? openDeleteDialog : undefined}
              />
            )}
          </div>
        </div>
        <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && closeDeleteDialog()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete project?</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <span className="font-semibold text-[#071426]">{deleteProjectName}</span>? {isMockMode ? 'This demo delete will remove it from the current listing.' : 'This action cannot be undone.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#26384D]" htmlFor="delete-confirm">
                Type <span className="text-red-600">delete</span> to delete
              </label>
              <Input
                id="delete-confirm"
                value={deleteConfirmText}
                onChange={(event) => setDeleteConfirmText(event.target.value)}
                placeholder="delete"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDeleteDialog}>
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-red-600 text-white hover:bg-red-700"
                disabled={deleteConfirmText.trim().toLowerCase() !== 'delete' || isDeleting}
                onClick={handleDeleteConfirmed}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-80px)] overflow-y-auto bg-gradient-to-br from-[#EEF5FF] via-white to-[#EAF7F3] px-8 py-10">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 lg:grid-cols-2">
        <SelectionCard
          icon={Search}
          badge="Decision Support"
          title="Distribution Analytics"
          description={<>Compare the current package configuration against package specifications and prior test results to identify matching specifications that may reduce redundant transit testing.</>}
          points={[
            "Find specifications similar to the current package",
            "Assess whether prior test results cover the new configuration",
            "Review trends and KPIs to support packaging decisions",
          ]}
          onOpen={() => setPendingModule('distribution')}
          buttonLabel="Open Distribution Analytics"
          tone="blue"
        />
        <SelectionCard
          icon={Truck}
          badge="Data Collection"
          title="Data Collection Module"
          description={<>Record and submit ship test results, built for real-time data entry.</>}
          points={[
            "Log a new ship test and corresponding lean, walk and compression values.",
            "Record damage or quality levels.",
            "Generate test synopsis",
          ]}
          onOpen={() => setPendingModule('test-log')}
          buttonLabel="Open Data Collection"
          tone="green"
        />
      </div>
      <Dialog open={Boolean(pendingModule)} onOpenChange={(open) => !open && closeModuleDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl">Open {pendingModule === 'distribution' ? 'Distribution Analytics' : 'Data Collection Module'}?</DialogTitle>
            <DialogDescription className="text-base leading-7">
              {moduleDialogDescription}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModuleDialog}>
              Cancel
            </Button>
            <Button type="button" className="bg-blue text-white hover:bg-[#003B7D]" onClick={confirmModuleOpen}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default Page;
